import React, { useState } from 'react';

import { Autocomplete as MuiAutocomplete, Collapse, Stack, Typography, Box, List, Paper, styled } from '@mui/material';
import Icon from '../Icon';

import TextField from '../TextField';
import { WrappedAutoCompleteProps } from './types';

const CustomPaper = styled(Paper)({
  boxShadow: '0px 8px 24px 0px rgba(131, 60, 226, 0.16)'
});

export default function WrappedAutoComplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  label,
  getOptionDisabled,
  helperText,
  error,
  value,
  required,
  handleChange,
  options,
  placeholder,
  name,
  ...props
}: WrappedAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const [openGroup, setOpenGroup] = useState('');

  const handleGroupToggle = (groupTitle: string) => {
    setOpenGroup(prevOpenGroup => (prevOpenGroup === groupTitle ? '' : groupTitle));
  };

  return (
    <MuiAutocomplete
      popupIcon={<Icon name="arrow-down-1-outline" size="24" fill="#292D32" styles={{ opacity: '1' }} />}
      options={options}
      PaperComponent={props => <CustomPaper {...props} />}
      ChipProps={{ variant: 'outlined', sx: { borderRadius: '8px' } }}
      onChange={(_: any, value) => {
        handleChange(name, value);
      }}
      value={value}
      isOptionEqualToValue={(option: any, value: any) =>
        value && typeof value === 'string' ? option.value === value : option.value === value?.value
      }
      renderInput={params => (
        <TextField
          variant="outlined"
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}
      getOptionDisabled={getOptionDisabled}
      {...(props.groupBy && {
        renderGroup: params => (
          <li key={params.key} style={{ marginBottom: 16 }}>
            <Box onClick={() => handleGroupToggle(params.group)} sx={{ padding: '4px 10px' }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1">{params.group}</Typography>
                <Icon
                  name={openGroup === params.group ? 'arrow-up-2-outline' : 'arrow-down-1-outline'}
                  fill="#292D32"
                />
              </Stack>
            </Box>
            <Collapse in={openGroup === params.group} unmountOnExit>
              <List component="ul" disablePadding>
                {params.children}
              </List>
            </Collapse>
          </li>
        )
      })}
      {...props}
    />
  );
}
WrappedAutoComplete.displayName = 'Autocomplete';
